// Bundled stub of confessional documents commonly cited in reformed +
// evangelical seminary curricula. Spanish translations are abbreviated and
// drawn from public-domain editions. Expand as needed.

export interface ConfessionEntry {
  /** Confession or catechism identifier (e.g. "WSC", "Heid", "Belgic"). */
  source: string;
  /** Full display name. */
  sourceName: string;
  /** Reference number (question or article). */
  ref: string;
  /** Title or short prompt. */
  title: string;
  /** Full text or excerpted answer. */
  text: string;
}

export const CONFESSIONS: ConfessionEntry[] = [
  // ─── Westminster Shorter Catechism (selected) ─────────────────────────────
  {
    source: 'WSC',
    sourceName: 'Catecismo Menor de Westminster',
    ref: '1',
    title: '¿Cuál es el fin principal del hombre?',
    text: 'El fin principal del hombre es glorificar a Dios y gozar de Él para siempre.',
  },
  {
    source: 'WSC',
    sourceName: 'Catecismo Menor de Westminster',
    ref: '2',
    title: '¿Qué regla nos ha dado Dios para que le glorifiquemos y gocemos de Él?',
    text: 'La Palabra de Dios, contenida en las Escrituras del Antiguo y Nuevo Testamento, es la única regla para dirigirnos en cómo glorificar a Dios y gozar de Él.',
  },
  {
    source: 'WSC',
    sourceName: 'Catecismo Menor de Westminster',
    ref: '4',
    title: '¿Qué es Dios?',
    text: 'Dios es Espíritu, infinito, eterno e inmutable en su ser, sabiduría, poder, santidad, justicia, bondad y verdad.',
  },
  {
    source: 'WSC',
    sourceName: 'Catecismo Menor de Westminster',
    ref: '14',
    title: '¿Qué es el pecado?',
    text: 'El pecado es cualquier falta de conformidad con la ley de Dios, o transgresión de ella.',
  },
  {
    source: 'WSC',
    sourceName: 'Catecismo Menor de Westminster',
    ref: '21',
    title: '¿Quién es el Redentor de los escogidos de Dios?',
    text: 'El único Redentor de los escogidos de Dios es el Señor Jesucristo, quien siendo el eterno Hijo de Dios, llegó a ser hombre, y así fue y continúa siendo Dios y hombre en dos naturalezas distintas y una persona, por siempre.',
  },
  {
    source: 'WSC',
    sourceName: 'Catecismo Menor de Westminster',
    ref: '33',
    title: '¿Qué es la justificación?',
    text: 'La justificación es un acto de la libre gracia de Dios, por el cual perdona todos nuestros pecados y nos acepta como justos delante de Él, solamente en virtud de la justicia de Cristo imputada a nosotros y recibida por la fe solamente.',
  },

  // ─── Heidelberg Catechism (selected) ──────────────────────────────────────
  {
    source: 'Heid',
    sourceName: 'Catecismo de Heidelberg',
    ref: '1',
    title: '¿Cuál es tu único consuelo, tanto en la vida como en la muerte?',
    text: 'Que yo, con cuerpo y alma, así en la vida como en la muerte, no me pertenezco a mí mismo, sino a mi fiel Salvador Jesucristo, quien me ha redimido por completo con su preciosa sangre, librándome del dominio del diablo.',
  },
  {
    source: 'Heid',
    sourceName: 'Catecismo de Heidelberg',
    ref: '21',
    title: '¿Qué es la verdadera fe?',
    text: 'La verdadera fe no es solamente un conocimiento cierto, por el cual tengo por verdadero todo lo que Dios nos ha revelado en su Palabra, sino también una firme confianza que el Espíritu Santo obra en mí por medio del evangelio.',
  },
  {
    source: 'Heid',
    sourceName: 'Catecismo de Heidelberg',
    ref: '60',
    title: '¿Cómo eres justo delante de Dios?',
    text: 'Sólo por la verdadera fe en Jesucristo; aunque mi conciencia me acusa que he transgredido gravemente todos los mandamientos de Dios, Dios me imputa la perfecta satisfacción, justicia y santidad de Cristo, sin mérito alguno de mi parte, sólo por gracia.',
  },

  // ─── Belgic Confession (selected) ─────────────────────────────────────────
  {
    source: 'Belgic',
    sourceName: 'Confesión Belga',
    ref: '1',
    title: 'De que hay un solo Dios',
    text: 'Todos creemos de corazón y confesamos con la boca que hay un solo Ser, simple y espiritual, al cual llamamos Dios; el cual es eterno, incomprehensible, invisible, inmutable, infinito, todopoderoso, perfectamente sabio, justo y bueno, y fuente desbordante de todos los bienes.',
  },
  {
    source: 'Belgic',
    sourceName: 'Confesión Belga',
    ref: '7',
    title: 'De la suficiencia de la Escritura',
    text: 'Creemos que esta Sagrada Escritura contiene perfectamente la voluntad de Dios y enseña ampliamente todo lo que el hombre debe creer para ser salvo. Por tanto, es ilícito que cualquier hombre, aun apóstol, enseñe de manera distinta a lo que ya hemos recibido.',
  },

  // ─── 1689 Baptist Confession (selected) ───────────────────────────────────
  {
    source: '1689',
    sourceName: 'Confesión Bautista de 1689',
    ref: '1.1',
    title: 'De las Sagradas Escrituras',
    text: 'La Santa Escritura es la única regla suficiente, cierta e infalible de todo conocimiento, fe y obediencia que conduce a la salvación.',
  },
  {
    source: '1689',
    sourceName: 'Confesión Bautista de 1689',
    ref: '11.1',
    title: 'De la justificación',
    text: 'A los que Dios justifica eficazmente llama y también gratuitamente justifica, no infundiéndoles justicia, sino perdonándoles los pecados y contándolos y aceptándolos como justos por amor a Cristo solamente.',
  },

  // ─── Nicene Creed (anchor) ────────────────────────────────────────────────
  {
    source: 'Niceno',
    sourceName: 'Credo Niceno-Constantinopolitano',
    ref: '1',
    title: 'El Credo Niceno (381)',
    text: 'Creemos en un solo Dios, Padre Todopoderoso, Creador del cielo y de la tierra, de todo lo visible e invisible. Y en un solo Señor Jesucristo, Hijo único de Dios, nacido del Padre antes de todos los siglos: Dios de Dios, Luz de Luz, Dios verdadero de Dios verdadero, engendrado, no creado, de la misma naturaleza del Padre.',
  },
];

const BY_KEY = new Map<string, ConfessionEntry>();
for (const c of CONFESSIONS) BY_KEY.set(`${c.source.toLowerCase()}:${c.ref}`, c);

export function findConfession(source: string, ref: string): ConfessionEntry | undefined {
  return BY_KEY.get(`${source.toLowerCase()}:${ref}`);
}

// ─── Reference parser ───────────────────────────────────────────────────────
// Matches forms like:
//   WSC Q.1   WSC 1   WSC Q1
//   Heid Q.21 / Heid 21
//   Belgic Art.7 / Belgic 7
//   1689 1.1
//   Niceno 1

const PATTERN = /\b(WSC|Heid|Belgic|1689|Niceno)\s*(?:Q\.?|Art\.?)?\s*([0-9]+(?:\.[0-9]+)?)\b/gi;

export interface ConfessionMatch {
  source: string;
  ref: string;
  raw: string;
  start: number;
  end: number;
}

export function findConfessionMatches(text: string): ConfessionMatch[] {
  const out: ConfessionMatch[] = [];
  let m: RegExpExecArray | null;
  PATTERN.lastIndex = 0;
  while ((m = PATTERN.exec(text)) !== null) {
    const source = m[1]!;
    const ref = m[2]!;
    if (!findConfession(source, ref)) continue;
    out.push({
      source,
      ref,
      raw: m[0]!,
      start: m.index,
      end: m.index + m[0]!.length,
    });
  }
  return out;
}
